// Postal-code formats per country (reference data — no logic lives here).
export type PostalCodeConfig = {
  pattern: RegExp
  example: string
  format?: (value: string) => string
  uppercase?: boolean
}

export const POSTAL_CODE_CONFIGS: Record<string, PostalCodeConfig> = {
  Germany: {
    pattern: /^\d{5}$/,
    example: '10115',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Austria: {
    pattern: /^\d{4}$/,
    example: '1010',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Switzerland: {
    pattern: /^\d{4}$/,
    example: '8000',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  France: {
    pattern: /^\d{5}$/,
    example: '75001',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Netherlands: {
    pattern: /^\d{4}\s?[A-Z]{2}$/i,
    example: '1012 AB',
    format: (v) => {
      const clean = v.replace(/\s/g, '').toUpperCase()
      const digits = clean.replace(/\D/g, '').slice(0, 4)
      const letters = clean.replace(/[^A-Z]/g, '').slice(0, 2)
      if (digits.length === 4 && letters) {
        return `${digits} ${letters}`
      }
      return clean.slice(0, 7)
    },
    uppercase: true
  },
  Belgium: {
    pattern: /^\d{4}$/,
    example: '1000',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Luxembourg: {
    pattern: /^\d{4}$/,
    example: '1111',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Poland: {
    pattern: /^\d{2}-\d{3}$/,
    example: '00-001',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 5)
      if (digits.length > 2) {
        return `${digits.slice(0, 2)}-${digits.slice(2)}`
      }
      return digits
    }
  },
  'Czech Republic': {
    pattern: /^\d{3}\s?\d{2}$/,
    example: '110 00',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 5)
      if (digits.length > 3) {
        return `${digits.slice(0, 3)} ${digits.slice(3)}`
      }
      return digits
    }
  },
  Denmark: {
    pattern: /^\d{4}$/,
    example: '1000',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Italy: {
    pattern: /^\d{5}$/,
    example: '00100',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Spain: {
    pattern: /^\d{5}$/,
    example: '28001',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Portugal: {
    pattern: /^\d{4}-\d{3}$/,
    example: '1000-001',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 7)
      if (digits.length > 4) {
        return `${digits.slice(0, 4)}-${digits.slice(4)}`
      }
      return digits
    }
  },
  'United Kingdom': {
    pattern: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    example: 'SW1A 1AA',
    format: (v) => {
      // Basic UK postcode formatting - uppercase and add space before last 3 chars
      const clean = v.replace(/\s/g, '').toUpperCase().slice(0, 8)
      if (clean.length > 3) {
        return `${clean.slice(0, -3)} ${clean.slice(-3)}`
      }
      return clean
    },
    uppercase: true
  },
  Ireland: {
    pattern: /^[A-Z]\d{2}\s?[A-Z\d]{4}$/i,
    example: 'D02 AF30',
    format: (v) => {
      const clean = v.replace(/\s/g, '').toUpperCase().slice(0, 7)
      if (clean.length > 3) {
        return `${clean.slice(0, 3)} ${clean.slice(3)}`
      }
      return clean
    },
    uppercase: true
  },
  Sweden: {
    pattern: /^\d{3}\s?\d{2}$/,
    example: '111 22',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 5)
      if (digits.length > 3) {
        return `${digits.slice(0, 3)} ${digits.slice(3)}`
      }
      return digits
    }
  },
  Norway: {
    pattern: /^\d{4}$/,
    example: '0001',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Finland: {
    pattern: /^\d{5}$/,
    example: '00100',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Croatia: {
    pattern: /^\d{5}$/,
    example: '10000',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Hungary: {
    pattern: /^\d{4}$/,
    example: '1011',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Romania: {
    pattern: /^\d{6}$/,
    example: '010001',
    format: (v) => v.replace(/\D/g, '').slice(0, 6)
  },
  Bulgaria: {
    pattern: /^\d{4}$/,
    example: '1000',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Greece: {
    pattern: /^\d{3}\s?\d{2}$/,
    example: '104 31',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 5)
      if (digits.length > 3) {
        return `${digits.slice(0, 3)} ${digits.slice(3)}`
      }
      return digits
    }
  },
  Slovakia: {
    pattern: /^\d{3}\s?\d{2}$/,
    example: '811 01',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 5)
      if (digits.length > 3) {
        return `${digits.slice(0, 3)} ${digits.slice(3)}`
      }
      return digits
    }
  },
  Slovenia: {
    pattern: /^\d{4}$/,
    example: '1000',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Estonia: {
    pattern: /^\d{5}$/,
    example: '10111',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Latvia: {
    pattern: /^LV-?\d{4}$/i,
    example: 'LV-1001',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 4)
      return digits ? `LV-${digits}` : v.toUpperCase()
    },
    uppercase: true
  },
  Lithuania: {
    pattern: /^LT-?\d{5}$/i,
    example: 'LT-01001',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 5)
      return digits ? `LT-${digits}` : v.toUpperCase()
    },
    uppercase: true
  },
  Malta: {
    pattern: /^[A-Z]{3}\s?\d{4}$/i,
    example: 'VLT 1000',
    format: (v) => {
      const clean = v.replace(/\s/g, '').toUpperCase().slice(0, 7)
      if (clean.length > 3) {
        return `${clean.slice(0, 3)} ${clean.slice(3)}`
      }
      return clean
    },
    uppercase: true
  },
  Cyprus: {
    pattern: /^\d{4}$/,
    example: '1010',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Iceland: {
    pattern: /^\d{3}$/,
    example: '101',
    format: (v) => v.replace(/\D/g, '').slice(0, 3)
  },
  Serbia: {
    pattern: /^\d{5}$/,
    example: '11000',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  'Bosnia and Herzegovina': {
    pattern: /^\d{5}$/,
    example: '71000',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Montenegro: {
    pattern: /^\d{5}$/,
    example: '81000',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  'North Macedonia': {
    pattern: /^\d{4}$/,
    example: '1000',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Albania: {
    pattern: /^\d{4}$/,
    example: '1001',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Moldova: {
    pattern: /^MD-?\d{4}$/i,
    example: 'MD-2001',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 4)
      return digits ? `MD-${digits}` : v.toUpperCase()
    },
    uppercase: true
  },
  Kosovo: {
    pattern: /^\d{5}$/,
    example: '10000',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Ukraine: {
    pattern: /^\d{5}$/,
    example: '01001',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Belarus: {
    pattern: /^\d{6}$/,
    example: '220000',
    format: (v) => v.replace(/\D/g, '').slice(0, 6)
  },
  Liechtenstein: {
    pattern: /^\d{4}$/,
    example: '9490',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Monaco: {
    pattern: /^980\d{2}$/,
    example: '98000',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Andorra: {
    pattern: /^AD\d{3}$/i,
    example: 'AD500',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 3)
      return digits ? `AD${digits}` : v.toUpperCase()
    },
    uppercase: true
  },
  'San Marino': {
    pattern: /^4789\d$/,
    example: '47890',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Turkey: {
    pattern: /^\d{5}$/,
    example: '34000',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  Russia: {
    pattern: /^\d{6}$/,
    example: '101000',
    format: (v) => v.replace(/\D/g, '').slice(0, 6)
  },
  'United States': {
    pattern: /^\d{5}(-\d{4})?$/,
    example: '10001',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 9)
      if (digits.length > 5) {
        return `${digits.slice(0, 5)}-${digits.slice(5)}`
      }
      return digits.slice(0, 5)
    }
  },
  Canada: {
    pattern: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i,
    example: 'K1A 0B1',
    format: (v) => {
      const clean = v.replace(/\s/g, '').toUpperCase().slice(0, 6)
      if (clean.length > 3) {
        return `${clean.slice(0, 3)} ${clean.slice(3)}`
      }
      return clean
    },
    uppercase: true
  },
  Brazil: {
    pattern: /^\d{5}-?\d{3}$/,
    example: '01001-000',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 8)
      if (digits.length > 5) {
        return `${digits.slice(0, 5)}-${digits.slice(5)}`
      }
      return digits
    }
  },
  Australia: {
    pattern: /^\d{4}$/,
    example: '2000',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  India: {
    pattern: /^\d{6}$/,
    example: '110001',
    format: (v) => v.replace(/\D/g, '').slice(0, 6)
  },
  China: {
    pattern: /^\d{6}$/,
    example: '100000',
    format: (v) => v.replace(/\D/g, '').slice(0, 6)
  },
  Japan: {
    pattern: /^\d{3}-?\d{4}$/,
    example: '100-0001',
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 7)
      if (digits.length > 3) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`
      }
      return digits
    }
  },
  'South Korea': {
    pattern: /^\d{5}$/,
    example: '03000',
    format: (v) => v.replace(/\D/g, '').slice(0, 5)
  },
  'United Arab Emirates': {
    pattern: /^.+$/,
    example: ''
  },
  'South Africa': {
    pattern: /^\d{4}$/,
    example: '0001',
    format: (v) => v.replace(/\D/g, '').slice(0, 4)
  },
  Other: {
    pattern: /^.+$/,
    example: ''
  },
}

export const getPostalCodeExample = (country: string): string => POSTAL_CODE_CONFIGS[country]?.example || ''
